'use client'
import React, { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import Breadcrumb from '@/components/Breadcrumb'

export default function MediaMainComponent() {
   const tocRef = useRef(null)
   const [activeId, setActiveId] = useState(null)

   // Scroll Spy: Highlight active section
   useEffect(() => {
      const handleScroll = () => {
         const headings = document.querySelectorAll('h3[id], h4[id]')
         const scrollPosition = window.scrollY

         let currentActiveId = null
         headings.forEach((heading) => {
            const top = heading.offsetTop
            if (top <= scrollPosition + 200) {
               currentActiveId = heading.id
            }
         })

         setActiveId(currentActiveId)
      }

      window.addEventListener('scroll', handleScroll, { passive: true })
      handleScroll()

      return () => window.removeEventListener('scroll', handleScroll)
   }, [])

   // Smooth scroll to section
   const handleClick = (e, id) => {
      e.preventDefault()
      const target = document.getElementById(id)
      if (target) {
         window.scrollTo({
            top: target.offsetTop - 190,
            behavior: 'smooth',
         })
      }
   }

   // Apply active className to TOC links
   useEffect(() => {
      if (!tocRef.current) return
      const links = tocRef.current.querySelectorAll('a[href^="#"]')
      links.forEach((link) => {
         const href = link.getAttribute('href').slice(1)
         link.classList.toggle('link-active', href === activeId)
      })
   }, [activeId])

   return (
      <section id="case-study-body" className="cs-bg-secondary first-section">
         <Breadcrumb currentPage={'Blogs Details'} />
         <div className="container-fluid">
            <h1 className="case-study-title text-center">
               The War for Eyeballs: How AI-Generated Content, <br />
               Virtual Influencers & Interactive Media Are Disrupting
               Entertainment
            </h1>
            <div className="d-flex flex-column flex-sm-row my-4 gap-2 justify-content-center align-items-center">
               <div className="benz-profile-pic">
                  <svg
                     width="33"
                     height="31"
                     viewBox="0 0 33 31"
                     fill="none"
                     xmlns="http://www.w3.org/2000/svg"
                  >
                     <path
                        d="M16.9259 0.718543C16.9538 0.71867 16.9817 0.718798 17.0105 0.71893C17.4846 0.729083 17.8604 0.913657 18.1992 1.24253C18.2152 1.25666 18.2312 1.2708 18.2477 1.28536C18.526 1.53911 18.7122 1.9495 18.7404 2.32499C18.7538 2.85252 18.6748 3.32214 18.3174 3.73146C18.2785 3.77136 18.2391 3.81078 18.1992 3.84968C18.1803 3.86932 18.1615 3.88895 18.1421 3.90919C17.8716 4.17564 17.4825 4.36089 17.1001 4.36089C17.1001 5.01881 17.1001 5.67673 17.1001 6.35459C17.1633 6.37989 17.1633 6.37989 17.2279 6.40571C17.2724 6.42885 17.3166 6.45278 17.3604 6.4773C17.3859 6.49156 17.4114 6.50583 17.4377 6.52053C17.4649 6.53588 17.4921 6.55124 17.5202 6.56706C17.5629 6.59106 17.5629 6.59106 17.6065 6.61554C17.8109 6.73086 18.0135 6.84899 18.2151 6.96898C18.4782 7.1254 18.7435 7.27779 19.0088 7.43038C19.3868 7.6478 19.7644 7.86588 20.1409 8.08586C20.4387 8.25975 20.7375 8.43178 21.0363 8.6039C21.3859 8.80522 21.7352 9.00701 22.0834 9.21052C22.3812 9.3844 22.6801 9.55644 22.9789 9.72855C23.3285 9.92988 23.6777 10.1317 24.026 10.3352C24.296 10.4928 24.5668 10.6488 24.8376 10.8049C25.004 10.9008 25.1704 10.9968 25.3367 11.0928C25.4758 11.173 25.6149 11.2532 25.754 11.3334C26.0471 11.5022 26.3399 11.6718 26.6319 11.8425C26.7702 11.9233 26.9087 12.0037 27.0475 12.0837C27.0704 12.0969 27.0704 12.0969 27.0938 12.1104C27.1681 12.1532 27.2425 12.1959 27.3168 12.2387C27.3434 12.2539 27.37 12.2692 27.3974 12.285C27.4209 12.2985 27.4444 12.312 27.4687 12.3259C27.5151 12.3533 27.5605 12.3825 27.6054 12.4124C27.6125 12.4788 27.6145 12.5391 27.6133 12.6056C27.6133 12.6259 27.6132 12.6462 27.6131 12.667C27.6128 12.7357 27.6119 12.8043 27.6111 12.873C27.6107 12.9224 27.6104 12.9718 27.6102 13.0213C27.6095 13.1291 27.6086 13.237 27.6075 13.3448C27.6058 13.5203 27.6045 13.6958 27.6034 13.8712C27.6022 14.0601 27.6009 14.249 27.5994 14.4379C27.5958 14.9067 27.5934 15.3754 27.5917 15.8442C27.5916 15.8784 27.5914 15.9126 27.5913 15.9468C27.5904 16.2081 27.5894 16.4694 27.5885 16.7307C27.5876 17.0001 27.5866 17.2694 27.5855 17.5388C27.5855 17.5567 27.5854 17.5746 27.5853 17.5931C27.583 18.174 27.5788 18.7549 27.5736 19.3358C27.5681 19.95 27.5651 20.5642 27.5646 21.1784C27.5643 21.5462 27.5619 21.9139 27.5569 22.2817C27.5537 22.5339 27.5529 22.7861 27.5541 23.0384C27.5546 23.1839 27.5542 23.3292 27.5507 23.4748C27.507 24.0347 27.507 24.0347 27.6966 24.5347C27.846 24.6719 28.0277 24.7449 28.2185 24.8078C28.3203 24.8433 28.4021 24.8925 28.4925 24.9508C28.5319 24.9743 28.5319 24.9743 28.5721 24.9983C28.5973 25.0134 28.6225 25.0284 28.6485 25.044C28.7057 25.0778 28.7629 25.1117 28.8201 25.1455C28.9023 25.1942 28.9845 25.243 29.0664 25.2923C29.141 25.3371 29.2158 25.3815 29.2907 25.4258C29.3128 25.4393 29.3349 25.4527 29.3577 25.4666C29.378 25.4785 29.3983 25.4904 29.4192 25.5027C29.4368 25.5132 29.4544 25.5237 29.4724 25.5345C29.5235 25.5564 29.5235 25.5564 29.5773 25.5301C29.6277 25.4972 29.6677 25.4613 29.7109 25.4194C30.0856 25.0864 30.5559 24.9626 31.0494 24.9837C31.5115 25.0185 31.9186 25.2436 32.2318 25.5759C32.2509 25.5962 32.2701 25.6164 32.2898 25.6372C32.5732 25.9635 32.7072 26.3835 32.703 26.8108C32.7028 26.8389 32.7026 26.8669 32.7024 26.8958C32.6981 27.1106 32.6734 27.2933 32.5896 27.493C32.5785 27.5195 32.5673 27.5461 32.5559 27.5735C32.472 27.7552 32.3646 27.9063 32.2318 28.0553C32.2157 28.0734 32.1997 28.0916 32.1831 28.1103C31.8627 28.4517 31.4116 28.635 30.9479 28.6535C30.4251 28.6615 29.9672 28.4618 29.5906 28.1003C29.3461 27.8458 29.1965 27.5531 29.1134 27.2118C29.1085 27.1928 29.1037 27.1738 29.0986 27.1542C29.0281 26.6951 29.1239 26.2974 29.3179 25.8827C29.301 25.8729 29.284 25.8632 29.2666 25.8531C28.9574 25.6751 28.6485 25.4968 28.3402 25.3171C28.3154 25.3027 28.2905 25.2883 28.2649 25.2734C28.1362 25.1986 28.0086 25.1225 27.8819 25.0443C27.8564 25.0287 27.8309 25.0131 27.8046 24.997C27.7561 24.9673 27.7078 24.9373 27.6597 24.9068C27.5366 24.8306 27.5366 24.8306 27.3967 24.8064C27.3047 24.8423 27.2224 24.8882 27.1389 24.9401C27.1001 24.9636 27.0613 24.987 27.0226 25.0103C27.0014 25.0231 26.9802 25.0359 26.9584 25.0491C26.8305 25.1257 26.701 25.1997 26.5718 25.274C26.5142 25.3072 26.4567 25.3403 26.3991 25.3735C26.3703 25.3901 26.3414 25.4067 26.3116 25.4238C26.2277 25.4722 26.1438 25.5207 26.0598 25.5691C25.7595 25.7427 25.4587 25.9154 25.1575 26.0874C24.8586 26.258 24.5605 26.4298 24.2625 26.602C24.0967 26.6978 23.9307 26.7935 23.7648 26.8892C23.627 26.9687 23.4891 27.0482 23.3514 27.1278C23.0507 27.3015 22.7497 27.4745 22.4481 27.6466C22.1492 27.8171 21.8511 27.989 21.5531 28.1611C21.3873 28.257 21.2213 28.3526 21.0554 28.4483C20.9176 28.5279 20.7797 28.6074 20.642 28.687C20.3413 28.8607 20.0403 29.0336 19.7387 29.2057C19.4398 29.3763 19.1417 29.5481 18.8437 29.7203C18.7056 29.8001 18.5674 29.8798 18.4292 29.9595C18.3743 29.9912 18.3194 30.0229 18.2645 30.0546C18.164 30.1125 18.0636 30.1704 17.9632 30.2283C17.9184 30.2541 17.8737 30.2799 17.8289 30.3057C17.7732 30.3379 17.7175 30.37 17.6618 30.4021C17.6234 30.4242 17.6234 30.4242 17.5843 30.4468C17.5469 30.4682 17.5469 30.4682 17.5088 30.49C17.4551 30.5213 17.402 30.5536 17.3493 30.5865C17.3224 30.6031 17.2954 30.6197 17.2676 30.6369C17.2434 30.6522 17.2192 30.6675 17.1943 30.6832C17.1121 30.7195 17.0818 30.7171 16.9978 30.688C16.9513 30.6658 16.9057 30.6416 16.8608 30.6162C16.8347 30.6015 16.8085 30.5867 16.7815 30.5715C16.7538 30.5557 16.7261 30.5398 16.6975 30.5235C16.6687 30.5071 16.6398 30.4908 16.6101 30.474C16.4379 30.3762 16.2666 30.277 16.0963 30.1758C15.9246 30.074 15.7511 29.9753 15.5776 29.8765C15.3517 29.7477 15.1263 29.6181 14.9019 29.4867C14.6822 29.358 14.4616 29.231 14.2405 29.1049C13.9255 28.9252 13.6112 28.7443 13.2971 28.5629C13.159 28.4831 13.0208 28.4034 12.8826 28.3237C12.8272 28.2917 12.7718 28.2598 12.7165 28.2278C12.6891 28.212 12.6616 28.1962 12.6334 28.1799C11.7196 27.6527 11.7196 27.6527 11.6366 27.6048C11.5811 27.5728 11.5257 27.5408 11.4703 27.5089C11.3325 27.4294 11.1946 27.3498 11.0568 27.2702C10.7551 27.0959 10.4528 26.9224 10.1501 26.7497C9.91903 26.6179 9.68897 26.4845 9.4594 26.3501C9.26457 26.2362 9.06888 26.1239 8.87284 26.0121C8.44903 25.7703 8.02612 25.5271 7.60461 25.2814C7.553 25.2513 7.50136 25.2213 7.44969 25.1913C7.33327 25.1236 7.21759 25.0551 7.10339 24.9838C7.08344 24.9715 7.06349 24.9592 7.04294 24.9465C7.00629 24.9238 6.96987 24.9008 6.93374 24.8773C6.84062 24.8191 6.84062 24.8191 6.73456 24.8223C6.70332 24.8411 6.70332 24.8411 6.67146 24.8602C6.64493 24.8744 6.61841 24.8885 6.59108 24.903C6.55978 24.9206 6.52853 24.9383 6.49733 24.9561C6.47108 24.971 6.47108 24.971 6.44431 24.9861C6.20459 25.1225 5.96679 25.2622 5.7288 25.4015C5.4979 25.5366 5.26681 25.6714 5.0356 25.806C5.00576 25.8234 4.97591 25.8408 4.94607 25.8582C4.86987 25.9026 4.7936 25.9469 4.7173 25.9911C4.67395 26.0163 4.63065 26.0415 4.58736 26.0668C4.56094 26.0821 4.53453 26.0973 4.50732 26.1131C4.47242 26.1334 4.47242 26.1334 4.43681 26.1541C4.37354 26.188 4.3104 26.2153 4.24323 26.2405C4.24847 26.2587 4.25371 26.2769 4.2591 26.2957C4.39621 26.7831 4.37326 27.2892 4.12831 27.7427C3.86372 28.1796 3.46711 28.5179 2.96521 28.6432C2.50284 28.7497 2.04193 28.686 1.62649 28.4579C1.47793 28.3649 1.35112 28.2553 1.22711 28.132C1.20925 28.115 1.19139 28.098 1.173 28.0805C0.859088 27.7599 0.708494 27.295 0.703125 26.854C0.708474 26.3783 0.883853 25.9549 1.20155 25.6015C1.2148 25.5859 1.22804 25.5703 1.24169 25.5542C1.47803 25.2941 1.78979 25.1561 2.12172 25.0647C2.14567 25.0579 2.14567 25.0579 2.1701 25.0509C2.60124 24.9568 3.07323 25.0535 3.44906 25.2696C3.69301 25.4273 3.92611 25.6225 4.06431 25.8827C4.17352 25.8587 4.26321 25.8078 4.35985 25.7533C4.3797 25.7421 4.39955 25.731 4.42 25.7196C4.63119 25.6005 4.83936 25.4762 5.04765 25.3521C5.27413 25.2173 5.50191 25.0848 5.73067 24.9539C5.78109 24.925 5.83085 24.8949 5.87994 24.8637C5.95756 24.8179 6.0333 24.7923 6.11957 24.7666C6.29439 24.7048 6.4351 24.6313 6.54366 24.4768C6.66252 24.1566 6.61069 23.756 6.60134 23.4218C6.59785 23.2745 6.59765 23.1271 6.59706 22.9798C6.59564 22.7246 6.59223 22.4695 6.58753 22.2144C6.58049 21.8281 6.57764 21.4418 6.57561 21.0554C6.57477 20.9002 6.57377 20.745 6.57276 20.5897C6.57264 20.5725 6.57253 20.5552 6.57242 20.5375C6.5697 20.1274 6.56526 19.7174 6.56049 19.3074C6.56028 19.2895 6.56008 19.2716 6.55986 19.2531C6.55522 18.853 6.55029 18.4529 6.5452 18.0528C6.54313 17.8895 6.54108 17.7262 6.53905 17.5629C6.53883 17.545 6.5386 17.5271 6.53837 17.5086C6.53327 17.0994 6.52873 16.6902 6.52613 16.2809C6.52602 16.2637 6.52591 16.2465 6.5258 16.2288C6.52461 16.0398 6.52358 15.8509 6.52268 15.662C6.52081 15.2937 6.51663 14.9255 6.50948 14.5572C6.50474 14.3048 6.50237 14.0525 6.50185 13.8001C6.50147 13.6545 6.5 13.5091 6.49559 13.3635C6.47001 12.4868 6.47001 12.4868 6.63456 12.3033C6.78371 12.1659 6.9656 12.0928 7.15641 12.0301C7.25853 11.9949 7.34215 11.946 7.43367 11.8891C7.48738 11.8585 7.5411 11.8279 7.59482 11.7973C7.84106 11.657 8.08631 11.515 8.33089 11.3718C8.52678 11.2573 8.72362 11.1445 8.92076 11.0321C9.16924 10.8905 9.41686 10.7475 9.66372 10.6031C9.93272 10.4458 10.2027 10.2903 10.4727 10.1347C10.6115 10.0548 10.7502 9.97473 10.8889 9.89469C10.9443 9.86274 10.9997 9.83079 11.055 9.79884C11.0825 9.78302 11.1099 9.76721 11.1381 9.75091C12.5503 8.93618 13.9625 8.12144 15.3747 7.30671C15.4022 7.29087 15.4296 7.27503 15.4579 7.25872C15.5129 7.22699 15.5679 7.19527 15.6229 7.16355C15.752 7.08906 15.8811 7.01457 16.0102 6.94007C16.0559 6.91368 16.1017 6.88729 16.1474 6.8609C16.2048 6.82779 16.2621 6.79468 16.3195 6.76156C16.3455 6.74657 16.3715 6.73157 16.3983 6.71613C16.4209 6.70304 16.4436 6.68995 16.467 6.67647C16.5377 6.63575 16.5377 6.63575 16.6302 6.59528C16.6784 6.56953 16.6784 6.56953 16.7167 6.53351C16.7354 6.45455 16.7409 6.40158 16.7393 6.32246C16.7396 6.30126 16.7399 6.28005 16.7402 6.2582C16.7409 6.1882 16.7403 6.11827 16.7397 6.04826C16.74 5.9997 16.7403 5.95113 16.7407 5.90257C16.7415 5.77482 16.7413 5.6471 16.7408 5.51934C16.7405 5.38896 16.7412 5.25858 16.7419 5.12819C16.743 4.87242 16.7429 4.61666 16.7422 4.36089C16.7187 4.35846 16.7187 4.35846 16.6947 4.35598C16.6741 4.35378 16.6534 4.35159 16.6322 4.34932C16.6117 4.34718 16.5913 4.34503 16.5703 4.34282C16.1656 4.29063 15.7966 4.02998 15.5318 3.73046C15.2113 3.31014 15.0649 2.83066 15.1223 2.30328C15.199 1.8131 15.4387 1.36743 15.842 1.07093C16.175 0.837652 16.519 0.716077 16.9259 0.718543ZM16.108 1.82562C15.8835 2.09931 15.8183 2.40333 15.8476 2.75059C15.9105 3.0826 16.0835 3.34692 16.3588 3.54296C16.6434 3.70011 16.9421 3.73955 17.2618 3.68064C17.5759 3.58733 17.8058 3.38968 17.9691 3.10843C18.1057 2.83132 18.1291 2.53621 18.0458 2.23938C17.9405 1.94524 17.7622 1.70909 17.4819 1.56523C16.9927 1.34106 16.48 1.42966 16.108 1.82562ZM17.1001 7.86265C17.1001 11.245 17.1001 14.6274 17.1001 18.1123C17.1983 18.073 17.2816 18.0332 17.3726 17.9811C17.401 17.965 17.4293 17.9488 17.4585 17.9321C17.4892 17.9144 17.5199 17.8968 17.5506 17.8791C17.5826 17.8607 17.6147 17.8423 17.6468 17.8239C17.8918 17.6832 18.135 17.5395 18.3778 17.395C18.7101 17.1973 19.0455 17.005 19.3808 16.8125C19.69 16.6348 19.9983 16.456 20.3047 16.2736C20.5564 16.1237 20.8097 15.9771 21.0643 15.8323C21.2845 15.7071 21.5033 15.5797 21.7211 15.4503C21.8973 15.3458 22.0745 15.2431 22.2521 15.1409C22.5065 14.9946 22.7601 14.8467 23.0132 14.698C23.2323 14.5692 23.4519 14.4416 23.6723 14.315C23.897 14.1858 24.1205 14.0547 24.3432 13.922C24.5809 13.7805 24.82 13.6418 25.0605 13.5051C25.2766 13.3821 25.4922 13.2584 25.7059 13.1313C25.7273 13.1186 25.7487 13.106 25.7708 13.093C25.7901 13.0814 25.8093 13.0699 25.8292 13.058C25.8543 13.043 25.8543 13.043 25.8799 13.0277C25.9226 13.0018 25.9226 13.0018 25.9439 12.9491C25.9278 12.9402 25.9116 12.9312 25.895 12.922C25.5434 12.7265 25.194 12.5278 24.8469 12.3244C24.4399 12.0859 24.0303 11.8518 23.6204 11.6181C23.3882 11.4856 23.1569 11.3516 22.9261 11.2166C22.7406 11.1081 22.5548 11.0004 22.3687 10.8931C22.3411 10.8772 22.3135 10.8613 22.2851 10.845C22.2011 10.7966 22.1172 10.7482 22.0332 10.6998C21.71 10.5137 21.387 10.3274 21.0651 10.1391C20.8433 10.0094 20.6206 9.88133 20.3973 9.75411C20.1489 9.6125 19.9012 9.46953 19.6544 9.32512C19.3854 9.16782 19.1154 9.01225 18.8454 8.85672C18.7066 8.77677 18.5679 8.69672 18.4292 8.61668C18.2739 8.52705 18.1185 8.43743 17.9632 8.34786C17.9184 8.32206 17.8737 8.29626 17.8289 8.27046C17.7732 8.23834 17.7175 8.20622 17.6618 8.17411C17.6362 8.15937 17.6107 8.14462 17.5843 8.12943C17.5595 8.11513 17.5347 8.10083 17.5092 8.0861C17.4005 8.02313 17.2924 7.95936 17.1843 7.8955C17.1331 7.86057 17.1331 7.86057 17.1001 7.86265ZM16.5154 8.16937C16.315 8.28902 16.1128 8.40537 15.9099 8.52082C15.6839 8.64955 15.4586 8.7792 15.2342 8.91062C15.0145 9.03924 14.7939 9.16632 14.5728 9.29243C14.3403 9.42501 14.108 9.55798 13.8763 9.69181C13.849 9.70754 13.8218 9.72327 13.7937 9.73948C13.7115 9.78695 13.6293 9.83443 13.5472 9.88191C13.1907 10.0879 12.8338 10.2934 12.4761 10.4973C12.2701 10.6148 12.0648 10.7334 11.8602 10.8532C11.6415 10.9812 11.4221 11.1078 11.202 11.2334C11.1871 11.2419 11.1722 11.2504 11.1568 11.2592C11.0353 11.3286 10.9136 11.3979 10.792 11.4672C10.6858 11.5277 10.5797 11.5882 10.4736 11.6488C10.4586 11.6573 10.4437 11.6658 10.4284 11.6746C10.1983 11.8059 9.96919 11.9389 9.74055 12.0728C9.51609 12.204 9.29039 12.333 9.06452 12.4618C8.7518 12.6401 8.44004 12.8201 8.12839 13.0003C8.12839 13.0171 8.12839 13.034 8.12839 13.0514C8.17621 13.0866 8.17621 13.0866 8.23922 13.1212C8.27558 13.1422 8.27558 13.1422 8.31268 13.1636C8.33938 13.1788 8.36607 13.1939 8.39358 13.2095C8.42122 13.2254 8.44886 13.2414 8.47733 13.2577C8.53598 13.2915 8.59467 13.3251 8.65342 13.3587C8.79121 13.4375 8.92866 13.5168 9.06614 13.5961C9.0935 13.6119 9.12086 13.6277 9.14905 13.6439C9.37473 13.774 9.59949 13.9056 9.82406 14.0375C10.0431 14.1663 10.2628 14.2938 10.4831 14.4205C10.7079 14.5497 10.9314 14.6808 11.1541 14.8135C11.4058 14.9633 11.6591 15.1099 11.9137 15.2547C12.1339 15.38 12.3527 15.5073 12.5705 15.6367C12.7467 15.7413 12.9239 15.844 13.1015 15.9461C13.3559 16.0925 13.6095 16.2403 13.8626 16.3891C14.0817 16.5178 14.3014 16.6454 14.5217 16.772C14.7753 16.9178 15.0271 17.0664 15.2785 17.2161C15.5037 17.3499 15.7307 17.4801 15.9585 17.6093C16.1706 17.7297 16.3804 17.853 16.5873 17.9821C16.6598 18.0365 16.6598 18.0365 16.7422 18.0612C16.7422 14.7632 16.7422 11.4651 16.7422 8.06713C16.6558 8.06713 16.5877 8.12559 16.5154 8.16937ZM26.1093 13.7041C26.0852 13.7182 26.0611 13.7324 26.0363 13.747C26.01 13.7626 25.9838 13.7782 25.9567 13.7942C25.9004 13.8274 25.8442 13.8605 25.7879 13.8936C25.7733 13.9022 25.7588 13.9107 25.7438 13.9195C25.5912 14.0094 25.4377 14.0975 25.2842 14.1856C25.0567 14.3164 24.8306 14.4492 24.6052 14.5834C24.3564 14.7316 24.1059 14.8765 23.8541 15.0195C23.6334 15.145 23.4143 15.2729 23.1962 15.4029C22.9606 15.5434 22.7237 15.6811 22.4853 15.8167C22.2499 15.9505 22.0158 16.0862 21.7831 16.2245C21.6059 16.3297 21.4275 16.4329 21.2488 16.5356C21.0203 16.6669 20.7931 16.8001 20.5667 16.935C20.315 17.0848 20.0617 17.2314 19.807 17.3762C19.5869 17.5014 19.3682 17.6287 19.1505 17.7581C18.9716 17.8642 18.7916 17.9686 18.6113 18.0724C18.5701 18.0962 18.5701 18.0962 18.5281 18.1204C18.3995 18.1946 18.2707 18.2686 18.1418 18.3423C18.0703 18.3832 17.999 18.4243 17.9276 18.4654C17.8875 18.4881 17.8876 18.4881 17.8467 18.5113C17.8225 18.5253 17.7982 18.5393 17.7732 18.5537C17.7517 18.566 17.7303 18.5783 17.7082 18.591C17.6931 18.6017 17.678 18.6125 17.6624 18.6235C17.6624 18.6404 17.6624 18.6573 17.6624 18.6747C17.679 18.6843 17.6957 18.6939 17.7128 18.7038C17.7365 18.7174 17.7601 18.731 17.7844 18.745C17.811 18.7604 17.8375 18.7757 17.8649 18.7915C17.9246 18.8259 17.9843 18.8604 18.0441 18.8949C18.1942 18.9815 18.3444 19.0681 18.4948 19.1545C18.8045 19.3325 19.1126 19.5129 19.4195 19.6956C19.6426 19.8281 19.8675 19.9573 20.093 20.0856C20.3132 20.2108 20.532 20.3381 20.7497 20.4676C20.9269 20.5728 21.1053 20.6759 21.284 20.7786C21.5125 20.9099 21.7397 21.0431 21.9661 21.178C22.2178 21.3278 22.4711 21.4744 22.7257 21.6192C22.9459 21.7444 23.1647 21.8718 23.3824 22.0012C23.5596 22.1064 23.738 22.2095 23.9167 22.3122C24.1452 22.4435 24.3724 22.5767 24.5988 22.7116C24.865 22.8701 25.133 23.0248 25.4024 23.1778C25.6223 23.3029 25.8405 23.4308 26.0571 23.5616C26.0893 23.5809 26.0893 23.5809 26.1223 23.6006C26.141 23.6119 26.1597 23.6232 26.179 23.6349C26.224 23.6629 26.224 23.6629 26.2762 23.6589C26.2762 20.3524 26.2762 17.046 26.2762 13.6393C26.2057 13.6393 26.1699 13.6682 26.1093 13.7041ZM7.87279 13.716C7.87279 17.014 7.87279 20.312 7.87279 23.71C7.96495 23.6732 8.03985 23.6388 8.1239 23.5891C8.14773 23.5751 8.17157 23.5611 8.19612 23.5467C8.23456 23.524 8.23456 23.524 8.27377 23.5008C8.32934 23.4681 8.38492 23.4354 8.44051 23.4028C8.4692 23.386 8.49789 23.3691 8.52745 23.3517C8.66503 23.2712 8.80331 23.192 8.94153 23.1126C9.16894 22.9818 9.39507 22.849 9.62048 22.7148C9.87218 22.5649 10.1255 22.4183 10.3801 22.2735C10.6003 22.1483 10.8191 22.0209 11.0369 21.8915C11.2131 21.787 11.3903 21.6843 11.5679 21.5821C11.8223 21.4358 12.0759 21.2879 12.329 21.1392C12.548 21.0104 12.7677 20.8828 12.988 20.7562C13.2128 20.627 13.4363 20.4959 13.659 20.3632C13.9107 20.2134 14.164 20.0668 14.4186 19.922C14.6387 19.7968 14.8573 19.6696 15.0749 19.5402C15.256 19.4328 15.4382 19.3275 15.6208 19.2226C15.903 19.0604 16.1828 18.8946 16.4611 18.7258C16.3997 18.6579 16.3342 18.6164 16.2549 18.5712C16.2286 18.5561 16.2022 18.5411 16.1751 18.5255C16.1466 18.5094 16.1182 18.4932 16.0888 18.4766C16.059 18.4595 16.0291 18.4424 15.9992 18.4253C15.9371 18.3897 15.875 18.3542 15.8128 18.3188C15.6687 18.2365 15.5249 18.1537 15.3811 18.0708C15.3535 18.0549 15.3259 18.0389 15.2974 18.0225C15.0993 17.9082 14.9022 17.7922 14.7055 17.6754C14.5273 17.5699 14.3481 17.4663 14.1686 17.3631C13.9401 17.2318 13.7129 17.0986 13.4865 16.9637C13.2525 16.8244 13.0172 16.6877 12.7804 16.5532C12.5146 16.4021 12.2509 16.2478 11.9883 16.0914C11.764 15.958 11.5384 15.8271 11.3121 15.697C11.1027 15.5765 10.8938 15.455 10.6852 15.333C10.4996 15.2246 10.3133 15.1171 10.1269 15.0099C9.87062 14.8625 9.61611 14.7123 9.3621 14.561C9.13691 14.4273 8.90986 14.2971 8.68203 14.1679C8.47049 14.0478 8.26127 13.9249 8.05555 13.7951C7.9707 13.7362 7.9707 13.7362 7.87279 13.716ZM17.3301 18.9047C17.3301 22.2534 17.3301 25.602 17.3301 29.0521C17.4269 29.0134 17.5111 28.974 17.6016 28.9238C17.6294 28.9084 17.6572 28.893 17.6859 28.8771C17.7159 28.8604 17.7458 28.8436 17.7758 28.8269C17.7911 28.8183 17.8065 28.8098 17.8223 28.801C18.0498 28.6742 18.2752 28.5442 18.4989 28.4109C18.7611 28.2547 19.0257 28.1028 19.2902 27.9507C19.4293 27.8706 19.5682 27.7904 19.7072 27.7102C19.7626 27.6783 19.818 27.6463 19.8734 27.6144C19.9008 27.5986 19.9282 27.5827 19.9564 27.5664C20.2056 27.4227 20.2056 27.4227 20.2887 27.3747C20.3441 27.3428 20.3994 27.3109 20.4547 27.2789C20.5938 27.1987 20.7329 27.1185 20.872 27.0384C21.1402 26.8838 21.4081 26.7289 21.6754 26.5728C21.9705 26.4003 22.2665 26.2294 22.5628 26.0588C22.7015 25.9788 22.8402 25.8988 22.9789 25.8188C23.1456 25.7226 23.3124 25.6264 23.4792 25.5303C23.7463 25.3764 24.0132 25.222 24.2793 25.0663C24.5161 24.9278 24.754 24.7914 24.9923 24.6556C25.2598 24.5031 25.5261 24.3485 25.7919 24.1929C25.8933 24.1335 25.9951 24.0748 26.0973 24.0168C26.0124 23.9204 25.9137 23.8652 25.8017 23.8059C25.6002 23.6966 25.4027 23.5816 25.2059 23.464C24.9677 23.3219 24.7282 23.1824 24.487 23.0455C24.2502 22.9109 24.0149 22.7742 23.7809 22.6349C23.4472 22.4363 23.1112 22.242 22.7745 22.0486C22.4353 21.8536 22.0966 21.658 21.7601 21.4584C21.582 21.3528 21.4028 21.2492 21.2233 21.146C20.9948 21.0147 20.7675 20.8815 20.5411 20.7466C20.2715 20.5861 20 20.4295 19.7273 20.2743C19.5348 20.1647 19.3432 20.0537 19.1526 19.9409C19.0178 19.8612 18.8824 19.7823 18.7471 19.7035C18.7212 19.6884 18.6954 19.6733 18.6687 19.6577C18.5889 19.6112 18.509 19.5647 18.4292 19.5181C18.3493 19.4716 18.2694 19.425 18.1895 19.3784C18.1379 19.3483 18.0862 19.3182 18.0345 19.2881C17.9169 19.2196 17.7994 19.1507 17.6825 19.081C17.6622 19.069 17.6419 19.0569 17.621 19.0445C17.5836 19.0222 17.5463 18.9998 17.509 18.9774C17.4254 18.923 17.4254 18.923 17.3301 18.9047ZM16.5123 19.0917C16.4904 19.1047 16.4686 19.1177 16.4461 19.1311C16.422 19.1455 16.398 19.1599 16.3732 19.1747C16.159 19.302 15.9439 19.4276 15.7278 19.5517C15.4983 19.6836 15.27 19.8173 15.0425 19.9527C14.6804 20.168 14.3156 20.3782 13.9503 20.588C13.6411 20.7656 13.3328 20.9444 13.0264 21.1268C12.7747 21.2767 12.5214 21.4233 12.2667 21.5681C12.0466 21.6933 11.8278 21.8206 11.6101 21.9501C11.4329 22.0553 11.2545 22.1584 11.0758 22.2611C10.8473 22.3924 10.6201 22.5256 10.3937 22.6605C10.142 22.8103 9.88867 22.9569 9.63403 23.1017C9.41438 23.2267 9.19609 23.3537 8.97888 23.4828C8.68854 23.6551 8.39559 23.823 8.10283 23.9912C8.10283 24.0081 8.10283 24.0249 8.10283 24.0423C8.1213 24.0528 8.13977 24.0633 8.1588 24.0742C8.48175 24.2582 8.80451 24.4424 9.12525 24.6302C9.34494 24.7588 9.56547 24.8859 9.78662 25.012C10.1017 25.1917 10.416 25.3725 10.73 25.554C10.8681 25.6338 11.0063 25.7135 11.1445 25.7932C11.3101 25.8887 11.4756 25.9842 11.6411 26.0799C11.9429 26.2542 12.2451 26.4277 12.5479 26.6003C12.779 26.7321 13.009 26.8656 13.2386 27C13.4334 27.1139 13.6291 27.2262 13.8251 27.338C14.1402 27.5177 14.4545 27.6985 14.7685 27.88C14.8789 27.9438 14.9894 28.0075 15.0999 28.0712C15.1551 28.1031 15.2104 28.135 15.2656 28.1668C15.4069 28.2484 15.5482 28.3298 15.6896 28.4112C15.747 28.4443 15.8045 28.4775 15.862 28.5106C15.8895 28.5264 15.9171 28.5423 15.9454 28.5587C16.1282 28.6641 16.3104 28.7704 16.4915 28.8785C16.5613 28.928 16.5613 28.928 16.64 28.9499C16.64 25.6772 16.64 22.4044 16.64 19.0325C16.5924 19.0325 16.552 19.0679 16.5123 19.0917ZM30.0282 26.1513C29.8406 26.4123 29.7682 26.72 29.8154 27.0356C29.8763 27.3428 30.0179 27.5776 30.2636 27.7741C30.5638 27.9611 30.863 28.0248 31.2094 27.9499C31.3634 27.908 31.4876 27.8388 31.6183 27.7486C31.6523 27.7264 31.6523 27.7264 31.687 27.7038C31.921 27.4762 32.0469 27.1872 32.0592 26.8635C32.0552 26.5289 31.9329 26.2594 31.7076 26.0189C31.6103 25.9279 31.5078 25.864 31.3883 25.806C31.3714 25.7973 31.3545 25.7886 31.3372 25.7797C30.8727 25.5829 30.3375 25.7847 30.0282 26.1513ZM1.65365 26.2082C1.47109 26.4533 1.40672 26.7474 1.44268 27.0493C1.5038 27.3683 1.64414 27.637 1.91564 27.8265C2.21494 28.0072 2.52746 28.0581 2.87066 27.9866C3.16752 27.8858 3.41606 27.6964 3.56109 27.4179C3.70725 27.103 3.72565 26.8053 3.61081 26.4724C3.46981 26.1278 3.25295 25.9501 2.91409 25.806C2.44367 25.6252 1.96106 25.8437 1.65365 26.2082Z"
                        fill="#F1ECDC"
                     />
                  </svg>
               </div>
               <div className="benz-profile-name">Benzatine Author</div>
               <div className="vertical-green-line mx-3 d-sm-block d-none"></div>
               <div className="horizontal-green-line my-2 d-sm-none"></div>
               <div className="benz-profile-time">
                  Last Updated: February 19, 2025
               </div>
            </div>
            <div className="container-fluid">
               {/* Your existing layout */}
               <div className="case-study-container row gap-5 align-items-baseline mx-2">
                  <div className="col-2" id="toco">
                     <div className="rounded-5 p-3 octo">
                        <div ref={tocRef} id="toc-container">
                           <div className="toc-title-parent">
                              <h3 className="toc-main-title m-0">
                                 The Battle for Attention in a Digital World
                              </h3>
                           </div>
                           <div className="ps-3">
                              <img
                                 src="/assets/svg/case-study/case-study-spacer.svg"
                                 className="w-100 mt-3"
                                 alt="spacer"
                              />
                              <h3 className="my-1">
                                 <Link
                                    onClick={(e) =>
                                       handleClick(
                                          e,
                                          'The Rise of AI-Generated Content'
                                       )
                                    }
                                    className="a2"
                                    href="#The Rise of AI-Generated Content"
                                 >
                                    The Rise of AI-Generated Content
                                 </Link>
                              </h3>
                              <img
                                 src="/assets/svg/case-study/case-study-spacer.svg"
                                 className="w-100 my-3"
                                 alt="spacer"
                              />
                              <h3 className="m-0">
                                 <Link
                                    onClick={(e) =>
                                       handleClick(
                                          e,
                                          'Virtual Influencers & AI-Generated Personalities'
                                       )
                                    }
                                    className="a2"
                                    href="#Virtual Influencers & AI-Generated Personalities"
                                 >
                                    Virtual Influencers & AI-Generated
                                    Personalities
                                 </Link>
                              </h3>
                              <img
                                 src="/assets/svg/case-study/case-study-spacer.svg"
                                 className="w-100"
                                 alt="spacer"
                              />
                              <h3 className="m-0">
                                 <Link
                                    onClick={(e) =>
                                       handleClick(e, 'InteractiveMedia')
                                    }
                                    className="a2"
                                    href="#InteractiveMedia"
                                 >
                                    Interactive Media: The Shift from Passive to
                                    Personalized Entertainment
                                 </Link>
                              </h3>
                              <img
                                 src="/assets/svg/case-study/case-study-spacer.svg"
                                 className="w-100"
                                 alt="spacer"
                              />
                              <h3 className="m-0">
                                 <Link
                                    onClick={(e) => handleClick(e, 'Hollywood')}
                                    className="a2"
                                    href="#Hollywood"
                                 >
                                    The Business Shift: Hollywood, Streaming &
                                    Content Creators
                                 </Link>
                              </h3>
                              <img
                                 src="/assets/svg/case-study/case-study-spacer.svg"
                                 className="w-100"
                                 alt="spacer"
                              />

                              <ol className="level-1 mt-3">
                                 <div>
                                    <Link
                                       onClick={(e) =>
                                          handleClick(
                                             e,
                                             'Ethical & Industry Challenges'
                                          )
                                       }
                                       // className="a2"
                                       href="#Ethical & Industry Challenges"
                                       className="Bloglevel-1 a2"
                                    >
                                       Ethical & Industry Challenges
                                    </Link>
                                 </div>
                                 <li>
                                    <Link
                                       onClick={(e) =>
                                          handleClick(
                                             e,
                                             'Death of Human Creativity?'
                                          )
                                       }
                                       href="#Death of Human Creativity?"
                                    >
                                       The Death of Human Creativity?
                                    </Link>
                                 </li>
                                 <li>
                                    <Link
                                       onClick={(e) =>
                                          handleClick(
                                             e,
                                             'Legal & Copyright Issues'
                                          )
                                       }
                                       href="#Legal & Copyright Issues"
                                    >
                                       Legal & Copyright Issues
                                    </Link>
                                 </li>
                                 <li>
                                    <Link
                                       onClick={(e) =>
                                          handleClick(e, 'The Deepfake Dilemma')
                                       }
                                       href="#The Deepfake Dilemma"
                                    >
                                       The Deepfake Dilemma
                                    </Link>
                                 </li>
                              </ol>

                              <img
                                 src="/assets/svg/case-study/case-study-spacer.svg"
                                 className="w-100"
                                 alt="spacer"
                              />
                              <h3 className="my-1">
                                 <Link
                                    onClick={(e) =>
                                       handleClick(
                                          e,
                                          'Who Will Win the War for Eyeballs'
                                       )
                                    }
                                    className="a2"
                                    href="#Who Will Win the War for Eyeballs"
                                 >
                                    Who Will Win the War for Eyeballs?
                                 </Link>
                              </h3>
                              <img
                                 src="/assets/svg/case-study/case-study-spacer.svg"
                                 className="w-100"
                                 alt="spacer"
                              />

                              <div className="mt-3 p-0">
                                 <h3>
                                    <Link
                                       onClick={(e) =>
                                          handleClick(e, 'Sources')
                                       }
                                       className="a2"
                                       href="#Sources"
                                    >
                                       Sources:
                                    </Link>
                                 </h3>
                                 <ul className="Dotlevel">
                                    <li>
                                       <Link
                                          onClick={(e) =>
                                             handleClick(e, 'OpenAI’s Sora')
                                          }
                                          href="#OpenAI’s Sora"
                                       >
                                          AI-Generated Video Challenges
                                          (OpenAI’s Sora)
                                       </Link>
                                    </li>
                                    <li>
                                       <Link
                                          onClick={(e) =>
                                             handleClick(
                                                e,
                                                'Hollywood’s Resistance to AI in Scriptwriting'
                                             )
                                          }
                                          href="#Hollywood’s Resistance to AI in Scriptwriting"
                                       >
                                          Hollywood’s Resistance to AI in
                                          Scriptwriting
                                       </Link>
                                    </li>
                                    <li>
                                       <Link
                                          onClick={(e) =>
                                             handleClick(
                                                e,
                                                'AI-Generated Movie Trailers and Industry Response'
                                             )
                                          }
                                          href="#AI-Generated Movie Trailers and Industry Response"
                                       >
                                          AI-Generated Movie Trailers and
                                          Industry Response
                                       </Link>
                                    </li>
                                    <li>
                                       <Link
                                          onClick={(e) =>
                                             handleClick(
                                                e,
                                                'How AI is Reshaping the Entertainment Industry'
                                             )
                                          }
                                          href="#How AI is Reshaping the Entertainment Industry"
                                       >
                                          How AI is Reshaping the Entertainment
                                          Industry
                                       </Link>
                                    </li>
                                    <li>
                                       <Link
                                          onClick={(e) =>
                                             handleClick(
                                                e,
                                                'The Rise of AI Virtual Influencers in Digital Marketing'
                                             )
                                          }
                                          href="#The Rise of AI Virtual Influencers in Digital Marketing"
                                       >
                                          The Rise of AI Virtual Influencers in
                                          Digital Marketing
                                       </Link>
                                    </li>
                                 </ul>
                              </div>
                           </div>
                        </div>
                     </div>
                     <div className="social-logos rounded-5 mt-4">
                        <h3 className="a2">
                           Found this post insightful? Don’t forget to share it
                           with your network!
                        </h3>
                        <img
                           src="/assets/svg/case-study/case-study-spacer.svg"
                           className="w-100 my-3"
                           alt="spacer"
                        />
                        <div className="d-flex gap-3">
                           <Link
                              target="_blank"
                              href={`http://www.linkedin.com/shareArticle?mini=true&amp;url=${process.env.NEXT_PUBLIC_DOMAIN_NAME}/blog-details/media-ent-details&amp;title=The War for Eyeballs: How AI-Generated Content, Virtual Influencers & Interactive Media Are Disrupting Entertainment`}
                              className="social-icon-bg"
                           >
                              <svg
                                 width="29"
                                 height="29"
                                 viewBox="0 0 29 29"
                                 fill="none"
                                 xmlns="http://www.w3.org/2000/svg"
                              >
                                 <path
                                    d="M6.68945 28.2145H0.877453V9.52747H6.68945V28.2145ZM3.75245 7.02747C1.93945 7.02747 0.439453 5.46447 0.439453 3.58947C0.439453 1.77747 1.93945 0.277466 3.75245 0.277466C5.62645 0.277466 7.12745 1.77747 7.12745 3.58947C7.12745 5.46447 5.62745 7.02747 3.75245 7.02747ZM28.3775 28.2145H22.6275V19.1525C22.6275 16.9645 22.5645 14.2145 19.5645 14.2145C16.5645 14.2145 16.1275 16.5275 16.1275 18.9645V28.2145H10.3145V9.52747H15.8775V12.0895H15.9395C16.7525 10.6525 18.6275 9.08947 21.4395 9.08947C27.3145 9.08947 28.4395 12.9645 28.4395 17.9645V28.2145H28.3775Z"
                                    fill="#F1ECDC"
                                 />
                              </svg>
                           </Link>
                           <Link
                              target="_blank"
                              href={`https://www.facebook.com/sharer/sharer.php?u=${process.env.NEXT_PUBLIC_DOMAIN_NAME}/blog-details/media-ent-details&amp;title=The War for Eyeballs: How AI-Generated Content, Virtual Influencers & Interactive Media Are Disrupting Entertainment`}
                              className="social-icon-bg"
                           >
                              <svg
                                 width="29"
                                 height="29"
                                 viewBox="0 0 29 29"
                                 fill="none"
                                 xmlns="http://www.w3.org/2000/svg"
                              >
                                 <path
                                    d="M28.4395 14.2145C28.4395 6.48248 22.1715 0.214478 14.4395 0.214478C6.70745 0.214478 0.439453 6.48248 0.439453 14.2145C0.439453 20.7805 4.95945 26.2895 11.0575 27.8025V18.4925H8.17045V14.2145H11.0575V12.3705C11.0575 7.60548 13.2135 5.39748 17.8925 5.39748C18.7795 5.39748 20.3095 5.57148 20.9355 5.74548V9.62348C20.6055 9.58848 20.0315 9.57148 19.3185 9.57148C17.0225 9.57148 16.1355 10.4415 16.1355 12.7015V14.2145H20.7085L19.9225 18.4925H16.1355V28.1125C23.0675 27.2745 28.4395 21.3715 28.4395 14.2145Z"
                                    fill="#F1ECDC"
                                 />
                                 <path
                                    d="M19.9229 18.4945L20.7089 14.2145H16.1359V12.7015C16.1359 10.4405 17.0229 9.57149 19.3179 9.57149C20.0309 9.57149 20.6049 9.58849 20.9349 9.62349V5.74449C20.3089 5.57049 18.7789 5.39749 17.8919 5.39749C13.2139 5.39749 11.0569 7.60549 11.0569 12.3705V14.2145H8.16992V18.4925H11.0569V27.8025C12.7168 28.2152 14.4382 28.3203 16.1359 28.1125V18.4925L19.9229 18.4945Z"
                                    fill="#1F473E"
                                 />
                              </svg>
                           </Link>
                           <Link
                              target="_blank"
                              href={`https://twitter.com/intent/tweet?url=${process.env.NEXT_PUBLIC_DOMAIN_NAME}/blog-details/media-ent-details&amp;text=The War for Eyeballs: How AI-Generated Content, Virtual Influencers & Interactive Media Are Disrupting Entertainment`}
                              className="social-icon-bg"
                           >
                              <svg
                                 width="26"
                                 height="26"
                                 viewBox="0 0 26 26"
                                 fill="none"
                                 xmlns="http://www.w3.org/2000/svg"
                              >
                                 <g clipPath="url(#clip0_2209_24115)">
                                    <mask
                                       id="mask0_2209_24115"
                                       style={{ maskType: 'luminance' }}
                                       maskUnits="userSpaceOnUse"
                                       x="0"
                                       y="0"
                                       width="26"
                                       height="26"
                                    >
                                       <path
                                          d="M0.939453 0.714478H25.9395V25.7145H0.939453V0.714478Z"
                                          fill="white"
                                       />
                                    </mask>
                                    <g mask="url(#mask0_2209_24115)">
                                       <path
                                          d="M15.766 11.3005L24.873 0.714478H22.715L14.807 9.90648L8.49201 0.714478H1.20801L10.758 14.6145L1.20801 25.7145H3.36601L11.716 16.0085L18.386 25.7145H25.67L15.766 11.3005ZM12.81 14.7365L11.842 13.3525L4.14301 2.33948H7.45901L13.672 11.2265L14.64 12.6105L22.717 24.1635H19.402L12.81 14.7365Z"
                                          fill="#F1ECDC"
                                       />
                                    </g>
                                 </g>
                                 <defs>
                                    <clipPath id="clip0_2209_24115">
                                       <rect
                                          width="25"
                                          height="25"
                                          fill="white"
                                          transform="translate(0.939453 0.714478)"
                                       />
                                    </clipPath>
                                 </defs>
                              </svg>
                           </Link>
                        </div>
                     </div>
                  </div>
                  {/* blogcontent */}
                  <div
                     id="blogcontent"
                     className="col rounded-5 px-md-5 py-md-4"
                  >
                     <div
                        className=""
                        id="The Battle for Attention in a Digital World"
                     >
                        <h3
                           id="The Battle for Attention in a Digital World"
                           className="content-title yellow-highlighter"
                        >
                           The Battle for Attention in a Digital World
                        </h3>
                        <p
                           id="The Battle for Attention in a Digital World"
                           className="content-text mt-3"
                        >
                           In 2025, human attention is the most valuable
                           currency. With social media, streaming platforms, and
                           interactive content fighting for every second of
                           audience engagement, the entertainment industry is
                           undergoing its biggest transformation yet.
                           AI-generated movies, virtual influencers, and
                           interactive media are redefining how content is
                           created, consumed, and monetized.
                        </p>
                        <p
                           id="The Battle for Attention in a Digital World"
                           className="content-text"
                        >
                           As AI becomes more advanced, it is no longer just a
                           tool—it is becoming a content creator, influencer,
                           and storyteller. From AI-driven deepfake actors to
                           personalized, real-time entertainment experiences,
                           this shift is disrupting Hollywood, gaming, and
                           digital platforms. But with this change comes an
                           important question: Will AI replace human creativity,
                           or will it simply enhance it?
                        </p>
                     </div>

                     <img
                        src="/assets/svg/case-study/case-study-spacer.svg"
                        className="w-100 my-4"
                        alt="spacer"
                     />
                     <div className="" id="The Rise of AI-Generated Content">
                        <h3
                           id="The Rise of AI-Generated Content"
                           className="content-title yellow-highlighter"
                        >
                           The Rise of AI-Generated Content
                        </h3>
                        <h3 className="center-content-text text-start mb-0 mt-3 title-with-grotesk-text">
                           Hollywood’s AI Experiment
                        </h3>
                        <p
                           id="The Rise of AI-Generated Content"
                           className="content-text mt-3"
                        >
                           Netflix and Disney have already started integrating
                           AI-generated scripts, voiceovers, and even
                           AI-enhanced animations. AI-powered platforms can
                           analyze viewer data to predict hit movies before they
                           are even made, helping studios make data-driven
                           creative decisions.
                        </p>
                        <h5 className="d-inline content-text dark-title-500">
                           Case Study:
                        </h5>
                        <p className="content-text d-inline">
                           In 2024, an AI-generated short film went viral,
                           sparking debates about whether AI can truly replace
                           human directors and screenwriters.
                        </p>
                        <p
                           id="The Rise of AI-Generated Content"
                           className="mt-3 content-text"
                        >
                           However, the rise of AI in filmmaking raises ethical
                           concerns: Should studios pay AI to generate movies
                           instead of hiring real actors, writers, and
                           directors? And who owns the rights to AI-generated
                           content?
                        </p>
                     </div>

                     <img
                        src="/assets/svg/case-study/case-study-spacer.svg"
                        className="w-100 my-4"
                        alt="spacer"
                     />

                     <div
                        className=""
                        id="Virtual Influencers & AI-Generated Personalities"
                     >
                        <h3
                           id="Virtual Influencers & AI-Generated Personalities"
                           className="content-title yellow-highlighter"
                        >
                           Virtual Influencers & AI-Generated Personalities
                        </h3>
                        <p
                           id="Virtual Influencers & AI-Generated Personalities"
                           className="mt-3 content-text"
                        >
                           The rise of virtual influencers—AI-generated
                           personalities who look, act, and engage like real
                           social media stars—has reshaped influencer marketing.
                           AI-driven influencers never age, never get caught in
                           scandals, and are programmed for maximum engagement.
                        </p>
                        <h3 className="center-content-text text-start m-0 title-with-grotesk-text">
                           The Growth of AI Influencers
                        </h3>
                        <p
                           id="Virtual Influencers & AI-Generated Personalities"
                           className="content-text mt-3"
                        >
                           Lil Miquela, an AI-generated Instagram model, has
                           secured brand deals with Prada, Samsung, and Calvin
                           Klein. Virtual influencers like FN Meka (a rap star
                           created entirely by AI) have millions of followers,
                           proving that audiences are willing to engage with
                           digital personalities as much as (or more than) real
                           humans.
                        </p>
                        <h5 className="d-inline content-text dark-title-500">
                           Case Study:
                        </h5>
                        <p className="content-text d-inline">
                           Coca-Cola’s AI-generated influencer campaign
                           increased engagement by 40%, outperforming
                           traditional celebrity endorsements.
                        </p>
                        <p
                           id="Virtual Influencers & AI-Generated Personalities"
                           className="mt-3 content-text"
                        >
                           The question remains: Will human influencers become
                           obsolete as brands turn to cost-effective, fully
                           controlled AI personalities?
                        </p>
                     </div>
                     <div className="" id="InteractiveMedia">
                        <h3
                           id="InteractiveMedia"
                           className="content-title yellow-highlighter"
                        >
                           Interactive Media: The Shift from Passive to
                           Personalized Entertainment
                        </h3>
                        <p id="InteractiveMedia" className="mt-3 content-text">
                           The traditional way of consuming media—watching a
                           fixed story—is fading. AI-powered entertainment is
                           interactive, dynamic, and personalized.
                        </p>
                        <h3 className="center-content-text text-start m-0 title-with-grotesk-text">
                           AI-Driven, Real-Time Storytelling
                        </h3>
                        <p id="InteractiveMedia" className="content-text mt-3">
                           Imagine watching a movie where the plot changes in
                           real-time based on your emotions and reactions. AI
                           can analyze facial expressions, heart rate, and
                           engagement levels to adjust scenes, characters, and
                           dialogue to match individual preferences.
                        </p>
                        <h5 className="d-inline content-text dark-title-500">
                           Gaming Industry:
                        </h5>
                        <p className="content-text d-inline">
                           AI is already revolutionizing video games. Fortnite
                           and Roblox are using AI-generated content to create
                           dynamic, ever-changing storylines for players. The
                           next step? Fully immersive, AI-driven games where no
                           two players experience the same story.
                        </p>
                        <p id="InteractiveMedia" className="mt-3 content-text">
                           The era of passive entertainment is ending—audiences
                           will soon be part of the content they consume.
                        </p>
                     </div>

                     <img
                        src="/assets/svg/case-study/case-study-spacer.svg"
                        className="w-100 my-4"
                        alt="spacer"
                     />
                     <div className="mt-3" id="Hollywood">
                        <h3
                           id="Hollywood"
                           className="content-title yellow-highlighter"
                        >
                           The Business Shift: Hollywood, Streaming & Content
                           Creators
                        </h3>
                        <p id="Hollywood" className="mt-3 content-text">
                           The Fall of Traditional Studios?
                        </p>
                        <p id="Hollywood" className="content-text">
                           Hollywood and major studios are struggling to adapt
                           as AI-generated content disrupts their traditional
                           business model.
                        </p>
                        <ul className="Dotlevel ps-5 mb-3" id="Hollywood">
                           <li>
                              AI can generate movies at a fraction of the cost
                              of Hollywood blockbusters.
                           </li>
                           <li>
                              Viewers are shifting to short-form, AI-driven
                              content on platforms like TikTok, YouTube, and
                              AI-powered streaming services.
                           </li>
                           <li>
                              Subscription fatigue is leading audiences to seek
                              personalized, AI-curated entertainment
                              experiences.
                           </li>
                        </ul>
                        <h5
                           className="d-inline content-text dark-title-500"
                           id="Hollywood"
                        >
                           Industry Prediction:
                        </h5>
                        <p className="content-text d-inline" id="Hollywood">
                           By 2030, at least 50% of digital content will be
                           AI-generated.
                        </p>
                     </div>

                     <div className="mt-3 mediatek-abcd">
                        <h3
                           id="Ethical & Industry Challenges"
                           className="content-title yellow-highlighter"
                        >
                           Ethical & Industry Challenges
                        </h3>

                        <h3
                           id="Death of Human Creativity?"
                           className="mt-3 dark-title-500 center-content-text text-start title-with-grotesk-text"
                        >
                           1 &nbsp; The Death of Human Creativity?
                        </h3>
                        <ul
                           className="Dotlevel"
                           id="Death of Human Creativity?"
                        >
                           <li>
                              If AI can write scripts, compose music, and
                              generate movies, what happens to human artists,
                              musicians, and filmmakers?
                           </li>
                           <li>
                              Will human creativity become obsolete, or will AI
                              simply serve as a tool for creative expansion?
                           </li>
                        </ul>
                        <h3
                           id="Legal & Copyright Issues"
                           className="mt-3 dark-title-500 center-content-text text-start title-with-grotesk-text"
                        >
                           2 &nbsp; Legal & Copyright Issues
                        </h3>
                        <ul className="Dotlevel" id="Legal & Copyright Issues">
                           <li>
                              Who owns AI-generated content? The creator of the
                              AI, the AI itself, or the platform?
                           </li>
                           <li>
                              Legal battles are already surfacing over the use
                              of AI-generated deepfake actors and voice
                              replicas.
                           </li>
                        </ul>
                        <h3
                           id="The Deepfake Dilemma"
                           className="mt-3 dark-title-500 center-content-text text-start title-with-grotesk-text"
                        >
                           3 &nbsp; The Deepfake Dilemma
                        </h3>
                        <ul className="Dotlevel" id="The Deepfake Dilemma">
                           <li>
                              AI-generated deepfakes can be used for
                              entertainment, but they also pose risks for
                              misinformation, fake news, and fraud.
                           </li>
                           <li>
                              Ethical concerns are rising over how AI-created
                              content is regulated.
                           </li>
                        </ul>
                     </div>
                     <img
                        src="/assets/svg/case-study/case-study-spacer.svg"
                        className="w-100 my-4"
                        alt="spacer"
                     />
                     <div className="" id="Who Will Win the War for Eyeballs">
                        <h3
                           id="Who Will Win the War for Eyeballs"
                           className="content-title yellow-highlighter"
                        >
                           Who Will Win the War for Eyeballs?
                        </h3>
                        <p
                           id="Who Will Win the War for Eyeballs"
                           className="content-text mt-3"
                        >
                           The entertainment industry is at a crossroads.
                           AI-generated content, virtual influencers, and
                           interactive media are redefining how we create and
                           consume entertainment. While AI is enhancing
                           efficiency and personalization, it also raises
                           ethical, creative, and business challenges.
                        </p>
                        <p
                           id="Who Will Win the War for Eyeballs"
                           className="content-text"
                        >
                           So, who will win this war for attention? Will AI take
                           over entertainment, or will human creativity find a
                           way to thrive alongside artificial intelligence? The
                           future of media belongs to those who can
                           adapt—whether human or AI.
                        </p>
                        <p
                           id="Who Will Win the War for Eyeballs"
                           className="content-text"
                        >
                           The next blockbuster hit may not come from
                           Hollywood—but from an AI model trained to understand
                           exactly what you want to see.
                        </p>
                     </div>

                     {/* <!-- Sources --> */}
                     <div className="sources2 mt-3">
                        <h3
                           id="Sources"
                           className="content-title yellow-highlighter"
                        >
                           Sources:
                        </h3>

                        <h5
                           id="OpenAI’s Sora"
                           className="content-text dark-title-500 mt-3"
                        >
                           AI-Generated Video Challenges (OpenAI’s Sora)
                        </h5>
                        <ul id="OpenAI’s Sora" className="Dotlevel">
                           <li>
                              OpenAI’s Sora has been criticized for biases and
                              ethical concerns, raising questions about the
                              future of AI-generated content in entertainment.
                           </li>
                           <li>
                              <Link
                                 target="_blank"
                                 href="https://www.wired.com/story/openai-sora-video-generator-bias/"
                              >
                                 https://www.wired.com/story/openai-sora-video-generator-bias/
                              </Link>
                           </li>
                        </ul>

                        <h5
                           id="Hollywood’s Resistance to AI in Scriptwriting"
                           className="content-text dark-title-500 mt-3"
                        >
                           Hollywood’s Resistance to AI in Scriptwriting
                        </h5>
                        <ul
                           id="Hollywood’s Resistance to AI in Scriptwriting"
                           className="Dotlevel"
                        >
                           <li>
                              Hollywood writers and directors are pushing back
                              against AI-generated scripts, with some refusing
                              to publish scripts to prevent unauthorized AI
                              training.
                           </li>
                           <li>
                              <Link
                                 target="_blank"
                                 href="https://www.theverge.com/news/632613/andor-tony-gilroy-ai-star-wars-training-copyright"
                              >
                                 https://www.theverge.com/news/632613/andor-tony-gilroy-ai-star-wars-training-copyright
                              </Link>
                           </li>
                        </ul>

                        <h5
                           id="AI-Generated Movie Trailers and Industry Response"
                           className="content-text dark-title-500 mt-3"
                        >
                           AI-Generated Movie Trailers and Industry Response
                        </h5>
                        <ul
                           id="AI-Generated Movie Trailers and Industry Response"
                           className="Dotlevel"
                        >
                           <li>
                              AI-generated trailer for "Ark: Survival Ascended"
                              DLC faced backlash for poor quality and lack of
                              developer involvement. Studio Wildcard distanced
                              itself, raising concerns about AI in content
                              creation.
                           </li>
                           <li>
                              <Link target="_blank" href="https://www.pcgamer.com/games/survival-crafting/ark-devs-distance-themselves-from-ai-generated-trailer-we-did-not-know-that-they-were-doing-it/?utm_source=chatgpt.com">
                                 https://www.pcgamer.com/games/survival-crafting/ark-devs-distance-themselves-from-ai-generated-trailer-we-did-not-know-that-they-were-doing-it/?utm_source=chatgpt.com
                              </Link>
                           </li>
                        </ul>

                        <h5
                           id="How AI is Reshaping the Entertainment Industry"
                           className="content-text dark-title-500 mt-3"
                        >
                           How AI is Reshaping the Entertainment Industry
                        </h5>
                        <ul
                           id="How AI is Reshaping the Entertainment Industry"
                           className="Dotlevel"
                        >
                           <li>
                              AI is being used to generate content across music,
                              movies, and gaming, forcing industry players to
                              adapt to a rapidly changing landscape.
                           </li>
                           <li>
                              <Link
                                 target="_blank"
                                 href="https://digitalcontentnext.org/blog/2025/01/28/how-ai-is-reshaping-the-entertainment-industry/"
                              >
                                 https://digitalcontentnext.org/blog/2025/01/28/how-ai-is-reshaping-the-entertainment-industry/
                              </Link>
                           </li>
                        </ul>

                        <h5
                           id="The Rise of AI Virtual Influencers in Digital Marketing"
                           className="content-text dark-title-500 mt-3"
                        >
                           The Rise of AI Virtual Influencers in Digital
                           Marketing
                        </h5>
                        <ul
                           id="The Rise of AI Virtual Influencers in Digital Marketing"
                           className="Dotlevel"
                        >
                           <li>
                              AI-generated influencers are becoming a dominant
                              force in marketing, offering brands new ways to
                              engage with audiences.
                           </li>
                           <li>
                              <Link
                                 target="_blank"
                                 href="https://aimodelagency.com/the-rise-of-ai-virtual-influencers-shaping-the-future-of-digital-marketing-in-2025"
                              >
                                 https://aimodelagency.com/the-rise-of-ai-virtual-influencers-shaping-the-future-of-digital-marketing-in-2025
                              </Link>
                           </li>
                        </ul>
                     </div>
                  </div>
               </div>
            </div>
         </div>
      </section>
   )
}
